'use client';

import { ArrowLeft } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { formatLocaleNumber } from '@kit/shared/utils';

export function AskAiPrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const year = formatLocaleNumber(new Date().getFullYear(), i18n.language, {
    useGrouping: false,
  });

  // Load Google Fonts dynamically
  useEffect(() => {
    if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(preconnect1);

      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect2);
    }

    if (!document.querySelector('link[href*="family=Outfit"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href =
        'https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
      document.head.appendChild(fontLink);
    }
  }, []);

  return (
    <>
      <style jsx global>{`
        .legal-page {
          font-family: 'Outfit', system-ui, sans-serif;
        }
        .legal-page .font-mono {
          font-family: 'JetBrains Mono', monospace;
        }
      `}</style>
      <div className="legal-page min-h-screen bg-[#0a0a0f] text-white">
        {/* Subtle gradient orbs */}
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-[#7B52F0]/10 blur-[120px]" />
          <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#6236C9]/10 blur-[120px]" />
        </div>

        {/* Header */}
        <header className="relative z-10 border-b border-gray-800/50 px-8 py-6">
          <nav className="mx-auto flex max-w-4xl items-center justify-between">
            <Link href="/" className="group flex items-center gap-3">
              <Image
                src="/images/askai/Logo.png"
                alt={t('common:brand.askAiLogoAlt')}
                width={145}
                height={32}
                className="h-8 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
            <Link
              href="/"
              className="flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common:back')}
            </Link>
          </nav>
        </header>

        {/* Content */}
        <main className="relative z-10 px-8 py-16">
          <div className="mx-auto max-w-4xl">
            <h1 className="mb-4 text-4xl font-light tracking-tight">
              {t('marketing:privacyPolicy')}
            </h1>
            <p className="mb-12 font-mono text-sm text-gray-500">
              {t('marketing:legal.lastUpdated')}
            </p>

            {isArabic ? <ArabicPrivacyBody /> : <EnglishPrivacyBody />}
          </div>
        </main>

        {/* Footer */}
        <footer className="relative z-10 border-t border-gray-800/50">
          <div className="mx-auto max-w-4xl px-8 py-8">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <p className="font-mono text-sm text-gray-600">
                &copy; {year} ASK AI. {t('marketing:legal.allRightsReserved')}
              </p>
              <div className="flex items-center gap-6">
                <span className="text-sm text-gray-400">
                  {t('marketing:legal.privacy')}
                </span>
                <Link
                  href="/terms-of-service"
                  className="text-sm text-gray-600 transition-colors hover:text-gray-400"
                >
                  {t('marketing:legal.terms')}
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

function EnglishPrivacyBody() {
  return (
    <div className="space-y-6 leading-relaxed text-gray-400">
      <p>
        At Ask AI (referred to as &quot;Ask AI&quot;/ &quot;We&quot;/
        &quot;Us&quot;/ &quot;Our&quot;), located in Al Wourud, Riyadh, Saudi
        Arabia, we are committed to providing secure and efficient AI-powered
        software solutions. Our services help businesses manage intelligent
        chatbots, booking platforms, and custom automation tools across various
        platforms, available through our web applications (the
        &quot;Website&quot;).
      </p>

      <p>
        This Privacy Policy (the &quot;Policy&quot;) outlines how we collect,
        use, and protect your personal information when you use our Website and
        services. We value your privacy and are dedicated to safeguarding your
        personal data. We encourage you to read this Policy carefully before
        using our Website or services or creating an account. By accessing our
        Website or using our services, you agree to the terms outlined in this
        Policy. If you do not agree with these terms, please refrain from using
        our Website and services.
      </p>

      <p>
        If we discover that we have inadvertently collected personal information
        from anyone under the age of 18 without parental or guardian consent, we
        will promptly delete such information. If you believe that we may have
        collected information from a minor without appropriate consent, please
        contact us at privacy@askai.sa.
      </p>

      <p>
        This Policy is designed in accordance with applicable Saudi Arabian
        privacy laws and regulations. These laws require us to inform you about
        the collection, use, storage, disclosure, and transfer of sensitive
        personal data or information.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        Information We Collect
      </h2>

      <p>
        When you interact with our Website, request a product demo, or sign up
        for our services, we collect certain types of information from you. The
        information we gather can be categorized as follows:
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Personal Data
      </h3>
      <p>
        This encompasses any information that can identify you as an individual,
        whether on its own or in conjunction with other details. Examples of
        personal data we collect include:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>Your name</li>
        <li>Phone number (including WhatsApp)</li>
        <li>Email address</li>
        <li>Company name and address</li>
        <li>Your job title</li>
        <li>Social media links</li>
        <li>
          Any other identifiers and sensitive data as outlined by relevant
          privacy regulations
        </li>
      </ul>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        User Activity Insights
      </h3>
      <p>
        We gather non-personal information about how you interact with our
        Website. This includes:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>The times and dates of your visits to our Website</li>
        <li>Your search history and usage patterns within the Website</li>
        <li>Device settings</li>
        <li>
          Your time zone, language preferences, screen resolution, and other
          settings
        </li>
        <li>The URL or advertisement that led you to our Website</li>
        <li>
          Technical details including your IP address, operating system, browser
          type, referring/exit pages, and unique device identifiers
        </li>
      </ul>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Behavioral Information
      </h3>
      <p>
        We monitor your interactions with our Website, collecting data about
        your activity across different devices using your email or social media
        login details. This includes tracking the services you search for, your
        opt-in choices, and your communication preferences.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Geolocation Information
      </h3>
      <p>
        With your consent, we may collect information about your location when
        you access our Website. This could involve GPS data or location details
        from your device. If you prefer not to share your exact location, you
        can turn off location services in your device settings.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Booking and Reservation Information
      </h3>
      <p>
        Our platforms allow you to manage bookings, including customer names,
        phone numbers, emails, and other relevant information, to facilitate
        reservations and appointments through our services.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Contacts and Media
      </h3>
      <p>
        Our platform allows you to upload contact lists, including names, phone
        numbers, emails, and other relevant information, to facilitate
        conversations through our services. Additionally, we collect information
        about your device&apos;s audio, video, and other media files, such as
        PDFs, enabling you to share these files with your customers via our
        platform.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Cookies and Tracking Tools
      </h3>
      <p>
        We, along with our partners, use cookies and similar technologies like
        web beacons to automatically collect information about your interactions
        with our Website. This helps us analyze which pages and advertisements
        you engage with, improve your user experience, enhance our services, and
        deliver relevant content across your devices.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Device Details
      </h3>
      <p>
        We collect details about the devices you use to access our Website, such
        as computers, phones, and other connected devices. This information
        includes your operating system, hardware and software versions, battery
        status, signal strength, available storage, browser type, and the names
        and types of apps or files on your device.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Data from External Providers
      </h3>
      <p>
        We may receive information about you from third-party services, such as
        advertising partners, data providers, and analytics companies.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Google User Data Usage and Protection
      </h3>
      <p>
        Our utilisation and transfer of information obtained from Google APIs
        will be conducted in strict accordance with the Google API Services User
        Data Policy, including the Limited Use requirements.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        Use and Safeguarding of Your Meta User Data
      </h3>
      <p>
        Similarly, our use and sharing of information acquired from Meta APIs
        will adhere to Meta&apos;s Platform Terms and Policies.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        Utilisation of Your Information
      </h2>
      <p>
        We collect and use your information primarily to enhance, manage, and
        maintain the Website, ensuring you can fully benefit from its features
        while adhering to our Terms of Use. Your data assists us in several
        ways, including offering recommendations, promoting the Website, and
        personalising your experience.
      </p>

      <p>Specifically, we may use the information gathered to:</p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>Organise and deliver services through the Website.</li>
        <li>
          Fulfil requests for services, provide support, and enhance Website
          functionality.
        </li>
        <li>
          Conduct internal operations such as troubleshooting, data analysis,
          research, and surveys.
        </li>
        <li>Communicate with you regarding changes to our services.</li>
        <li>Ensure quality control and improve our Website.</li>
        <li>
          Tailor your experience and enforce our terms, conditions, and
          policies.
        </li>
        <li>Verify your identity for access to certain features.</li>
        <li>
          Maintain security and detect any misuse, fraud, or unlawful activities
          on the Website.
        </li>
        <li>
          Provide location-based services, including personalised content, in
          line with your permissions.
        </li>
      </ul>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        Information We Share
      </h2>
      <p>
        We are committed to safeguarding your information and do not share it
        with third parties except under specific circumstances:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>Requests from Verified Government or Administrative Bodies</li>
        <li>Payment Verification with our payment gateway partner</li>
        <li>Fraud Monitoring in the event of suspicious activity</li>
      </ul>
      <p>
        In each case, we will limit the scope of disclosure to only what is
        necessary.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        How We Store Your Data
      </h2>
      <p>
        We protect all personal details using industry-standard encryption
        methods. All data is stored securely and protected according to best
        practices for data security.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        Cookie Policy
      </h2>
      <p>
        Cookies are used to enhance, secure, and personalise your experience
        with our Services. Specifically, we use cookies to:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>
          Enhance Services: Provide and improve our web-based Services,
          understand usage patterns, and customise your experience.
        </li>
        <li>
          Remember Preferences: Retain your choices, such as language
          preferences, to tailor our Services to your needs.
        </li>
        <li>Ensure Security: Mark your device as trusted to enhance security.</li>
      </ul>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        Business Transfers
      </h2>
      <p>
        In the event of a significant corporate event, such as the sale, merger,
        consolidation, or transfer of assets of our company, or in the unlikely
        scenario of bankruptcy, we may share all collected information as part of
        the transaction process.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        Updates to This Policy
      </h2>
      <p>
        We may revise this Privacy Policy from time to time. Any changes will be
        reflected by updating the &quot;Last Updated&quot; date at the top of
        this Policy. We encourage you to review this Policy regularly to stay
        informed about our privacy practices.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        How to Get in Touch
      </h2>
      <p>
        If you have any questions, concerns, or complaints about your personal
        information or our privacy practices, please contact us via:
      </p>
      <p className="mt-4 font-medium text-white">Ask AI</p>
      <p>Al Wourud</p>
      <p>Riyadh, Saudi Arabia</p>
      <p className="font-medium text-white">Email: privacy@askai.sa</p>
    </div>
  );
}

function ArabicPrivacyBody() {
  return (
    <div
      dir="rtl"
      className="space-y-6 text-start leading-relaxed text-gray-400"
    >
      <p>
        في Ask AI (المُشار إليها بـ &quot;Ask AI&quot;/ &quot;نحن&quot;/
        &quot;لنا&quot;/ &quot;الخاص بنا&quot;)، الواقعة في حي الورود، الرياض،
        المملكة العربية السعودية، نلتزم بتقديم حلول برمجية آمنة وفعّالة مدعومة
        بالذكاء الاصطناعي. تساعد خدماتنا الشركات على إدارة روبوتات المحادثة
        الذكية، ومنصات الحجز، وأدوات الأتمتة المخصّصة عبر منصات متعددة، وهي متاحة
        من خلال تطبيقاتنا على الويب (&quot;الموقع&quot;).
      </p>

      <p>
        توضّح سياسة الخصوصية هذه (&quot;السياسة&quot;) كيفية جمعنا لمعلوماتك
        الشخصية واستخدامها وحمايتها عند استخدامك لموقعنا وخدماتنا. نحن نقدّر
        خصوصيتك ونلتزم بحماية بياناتك الشخصية. نشجّعك على قراءة هذه السياسة بعناية
        قبل استخدام موقعنا أو خدماتنا أو إنشاء حساب. وباستخدامك لموقعنا أو
        خدماتنا، فإنك توافق على الشروط الموضّحة في هذه السياسة. وإذا كنت لا توافق
        على هذه الشروط، يُرجى الامتناع عن استخدام موقعنا وخدماتنا.
      </p>

      <p>
        إذا اكتشفنا أننا قمنا عن غير قصد بجمع معلومات شخصية من أي شخص دون سن
        الثامنة عشرة دون موافقة أحد الوالدين أو الوصي، فسنقوم بحذف هذه المعلومات
        على الفور. وإذا كنت تعتقد أننا قد جمعنا معلومات من قاصر دون الموافقة
        المناسبة، فيُرجى التواصل معنا عبر privacy@askai.sa.
      </p>

      <p>
        صُمِّمت هذه السياسة وفقًا لأنظمة ولوائح الخصوصية المعمول بها في المملكة
        العربية السعودية. تُلزمنا هذه الأنظمة بإبلاغك بشأن جمع البيانات أو
        المعلومات الشخصية الحساسة واستخدامها وتخزينها والإفصاح عنها ونقلها.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        المعلومات التي نجمعها
      </h2>

      <p>
        عند تفاعلك مع موقعنا، أو طلبك عرضًا توضيحيًا للمنتج، أو تسجيلك في خدماتنا،
        نقوم بجمع أنواع معيّنة من المعلومات منك. ويمكن تصنيف المعلومات التي نجمعها
        على النحو التالي:
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        البيانات الشخصية
      </h3>
      <p>
        يشمل ذلك أي معلومات يمكن أن تُعرّفك كفرد، سواء بمفردها أو بالاقتران
        بتفاصيل أخرى. ومن أمثلة البيانات الشخصية التي نجمعها:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>اسمك</li>
        <li>رقم الهاتف (بما في ذلك واتساب)</li>
        <li>عنوان البريد الإلكتروني</li>
        <li>اسم الشركة وعنوانها</li>
        <li>المسمّى الوظيفي الخاص بك</li>
        <li>روابط وسائل التواصل الاجتماعي</li>
        <li>
          أي مُعرّفات أخرى وبيانات حساسة كما هو موضّح في لوائح الخصوصية ذات الصلة
        </li>
      </ul>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        رؤى نشاط المستخدم
      </h3>
      <p>
        نجمع معلومات غير شخصية حول كيفية تفاعلك مع موقعنا. ويشمل ذلك:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>أوقات وتواريخ زياراتك لموقعنا</li>
        <li>سجل بحثك وأنماط استخدامك داخل الموقع</li>
        <li>إعدادات الجهاز</li>
        <li>
          منطقتك الزمنية، وتفضيلاتك اللغوية، ودقة الشاشة، والإعدادات الأخرى
        </li>
        <li>الرابط أو الإعلان الذي قادك إلى موقعنا</li>
        <li>
          التفاصيل التقنية بما في ذلك عنوان IP الخاص بك، ونظام التشغيل، ونوع
          المتصفح، وصفحات الإحالة/الخروج، ومُعرّفات الجهاز الفريدة
        </li>
      </ul>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        المعلومات السلوكية
      </h3>
      <p>
        نراقب تفاعلاتك مع موقعنا، ونجمع بيانات حول نشاطك عبر مختلف الأجهزة
        باستخدام بريدك الإلكتروني أو بيانات تسجيل الدخول عبر وسائل التواصل
        الاجتماعي. ويشمل ذلك تتبّع الخدمات التي تبحث عنها، وخياراتك في الاشتراك،
        وتفضيلاتك في التواصل.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        معلومات الموقع الجغرافي
      </h3>
      <p>
        بموافقتك، قد نجمع معلومات حول موقعك عند وصولك إلى موقعنا. وقد يتضمّن ذلك
        بيانات نظام تحديد المواقع العالمي (GPS) أو تفاصيل الموقع من جهازك. وإذا
        كنت تفضّل عدم مشاركة موقعك الدقيق، فيمكنك إيقاف خدمات الموقع في إعدادات
        جهازك.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        معلومات الحجز والمواعيد
      </h3>
      <p>
        تتيح لك منصاتنا إدارة الحجوزات، بما في ذلك أسماء العملاء، وأرقام الهواتف،
        وعناوين البريد الإلكتروني، وغيرها من المعلومات ذات الصلة، لتسهيل الحجوزات
        والمواعيد من خلال خدماتنا.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        جهات الاتصال والوسائط
      </h3>
      <p>
        تتيح لك منصّتنا تحميل قوائم جهات الاتصال، بما في ذلك الأسماء، وأرقام
        الهواتف، وعناوين البريد الإلكتروني، وغيرها من المعلومات ذات الصلة، لتسهيل
        المحادثات من خلال خدماتنا. بالإضافة إلى ذلك، نجمع معلومات حول ملفات الصوت
        والفيديو وملفات الوسائط الأخرى الموجودة على جهازك، مثل ملفات PDF، مما
        يتيح لك مشاركة هذه الملفات مع عملائك عبر منصّتنا.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        ملفات تعريف الارتباط وأدوات التتبّع
      </h3>
      <p>
        نستخدم، نحن وشركاؤنا، ملفات تعريف الارتباط والتقنيات المماثلة مثل إشارات
        الويب (web beacons) لجمع المعلومات تلقائيًا حول تفاعلاتك مع موقعنا. يساعدنا
        ذلك في تحليل الصفحات والإعلانات التي تتفاعل معها، وتحسين تجربتك كمستخدم،
        وتعزيز خدماتنا، وتقديم محتوى ذي صلة عبر أجهزتك.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        تفاصيل الجهاز
      </h3>
      <p>
        نجمع تفاصيل حول الأجهزة التي تستخدمها للوصول إلى موقعنا، مثل أجهزة
        الكمبيوتر والهواتف وغيرها من الأجهزة المتصلة. وتشمل هذه المعلومات نظام
        التشغيل، وإصدارات الأجهزة والبرامج، وحالة البطارية، وقوة الإشارة، ومساحة
        التخزين المتاحة، ونوع المتصفح، وأسماء وأنواع التطبيقات أو الملفات الموجودة
        على جهازك.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        البيانات من مزوّدين خارجيين
      </h3>
      <p>
        قد نتلقّى معلومات عنك من خدمات الأطراف الثالثة، مثل شركاء الإعلانات،
        ومزوّدي البيانات، وشركات التحليلات.
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        استخدام بيانات مستخدم Google وحمايتها
      </h3>
      <p>
        سيتم استخدامنا ونقلنا للمعلومات التي يتم الحصول عليها من واجهات برمجة
        تطبيقات Google (Google APIs) بما يتوافق تمامًا مع سياسة بيانات مستخدم
        خدمات Google API، بما في ذلك متطلبات الاستخدام المحدود (Limited Use).
      </p>

      <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
        استخدام بيانات مستخدم Meta الخاصة بك وحمايتها
      </h3>
      <p>
        وبالمثل، سيلتزم استخدامنا ومشاركتنا للمعلومات التي يتم الحصول عليها من
        واجهات برمجة تطبيقات Meta (Meta APIs) بشروط وسياسات منصة Meta.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        استخدام معلوماتك
      </h2>
      <p>
        نجمع معلوماتك ونستخدمها بشكل أساسي لتحسين الموقع وإدارته وصيانته، لضمان
        قدرتك على الاستفادة الكاملة من ميزاته مع الالتزام بشروط الاستخدام الخاصة
        بنا. تساعدنا بياناتك بعدة طرق، بما في ذلك تقديم التوصيات، والترويج للموقع،
        وتخصيص تجربتك.
      </p>

      <p>وعلى وجه التحديد، قد نستخدم المعلومات التي تم جمعها من أجل:</p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>تنظيم الخدمات وتقديمها من خلال الموقع.</li>
        <li>
          تلبية طلبات الخدمات، وتقديم الدعم، وتعزيز وظائف الموقع.
        </li>
        <li>
          إجراء العمليات الداخلية مثل استكشاف الأخطاء وإصلاحها، وتحليل البيانات،
          والبحث، والاستطلاعات.
        </li>
        <li>التواصل معك بشأن التغييرات على خدماتنا.</li>
        <li>ضمان مراقبة الجودة وتحسين موقعنا.</li>
        <li>تخصيص تجربتك وإنفاذ شروطنا وأحكامنا وسياساتنا.</li>
        <li>التحقّق من هويتك للوصول إلى ميزات معيّنة.</li>
        <li>
          الحفاظ على الأمان واكتشاف أي إساءة استخدام أو احتيال أو أنشطة غير
          قانونية على الموقع.
        </li>
        <li>
          تقديم الخدمات القائمة على الموقع، بما في ذلك المحتوى المخصّص، بما يتوافق
          مع الأذونات الممنوحة منك.
        </li>
      </ul>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        المعلومات التي نشاركها
      </h2>
      <p>
        نلتزم بحماية معلوماتك ولا نشاركها مع أطراف ثالثة إلا في ظروف محدّدة:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>الطلبات الواردة من جهات حكومية أو إدارية موثّقة</li>
        <li>التحقّق من الدفع مع شريك بوابة الدفع لدينا</li>
        <li>مراقبة الاحتيال في حال وجود نشاط مشبوه</li>
      </ul>
      <p>
        وفي كل حالة، سنحصر نطاق الإفصاح على ما هو ضروري فقط.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        كيفية تخزين بياناتك
      </h2>
      <p>
        نحمي جميع التفاصيل الشخصية باستخدام طرق التشفير المتوافقة مع معايير
        الصناعة. تُخزَّن جميع البيانات بشكل آمن وتُحمى وفقًا لأفضل الممارسات في
        مجال أمن البيانات.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        سياسة ملفات تعريف الارتباط
      </h2>
      <p>
        تُستخدم ملفات تعريف الارتباط لتحسين تجربتك مع خدماتنا وتأمينها وتخصيصها.
        وعلى وجه التحديد، نستخدم ملفات تعريف الارتباط من أجل:
      </p>
      <ul className="my-4 list-disc space-y-2 ps-6">
        <li>
          تحسين الخدمات: تقديم خدماتنا القائمة على الويب وتحسينها، وفهم أنماط
          الاستخدام، وتخصيص تجربتك.
        </li>
        <li>
          تذكّر التفضيلات: الاحتفاظ بخياراتك، مثل التفضيلات اللغوية، لتكييف خدماتنا
          مع احتياجاتك.
        </li>
        <li>ضمان الأمان: تمييز جهازك كجهاز موثوق به لتعزيز الأمان.</li>
      </ul>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        عمليات نقل الأعمال
      </h2>
      <p>
        في حال وقوع حدث مؤسّسي مهم، مثل بيع شركتنا أو اندماجها أو دمجها أو نقل
        أصولها، أو في السيناريو غير المرجّح المتمثّل في الإفلاس، فقد نشارك جميع
        المعلومات التي تم جمعها كجزء من عملية المعاملة.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        تحديثات هذه السياسة
      </h2>
      <p>
        قد نقوم بمراجعة سياسة الخصوصية هذه من وقت لآخر. وستنعكس أي تغييرات من خلال
        تحديث تاريخ &quot;آخر تحديث&quot; في أعلى هذه السياسة. ونشجّعك على مراجعة
        هذه السياسة بانتظام للبقاء على اطّلاع بممارساتنا المتعلّقة بالخصوصية.
      </p>

      <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
        كيفية التواصل
      </h2>
      <p>
        إذا كانت لديك أي أسئلة أو مخاوف أو شكاوى بشأن معلوماتك الشخصية أو ممارساتنا
        المتعلّقة بالخصوصية، فيُرجى التواصل معنا عبر:
      </p>
      <p className="mt-4 font-medium text-white">Ask AI</p>
      <p>حي الورود</p>
      <p>الرياض، المملكة العربية السعودية</p>
      <p className="font-medium text-white">
        البريد الإلكتروني: privacy@askai.sa
      </p>
    </div>
  );
}
